import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { downloadCsv } from "@/lib/csv";
import { toast } from "sonner";
import { Users, TrendingUp, Euro, Target, Sparkles, Flame, MailOpen, Download, Loader2 } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";

const STAGE_LABELS = {
  nouveaux: { label: "Nouveaux", color: "bg-slate-500", text: "text-slate-400" },
  contactes: { label: "Contactés", color: "bg-cyan-500", text: "text-cyan-400" },
  rdv: { label: "RDV planifiés", color: "bg-amber-500", text: "text-amber-400" },
  gagnes: { label: "Clients gagnés", color: "bg-emerald-500", text: "text-emerald-400" },
  perdus: { label: "Perdus", color: "bg-rose-500", text: "text-rose-400" },
};

function Stat({ icon: Icon, label, value, sub, accent = "cyan", testid }) {
  const accents = {
    cyan: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
    emerald: "text-emerald-400 bg-emerald-500/10 border-emerald-500/30",
    amber: "text-amber-400 bg-amber-500/10 border-amber-500/30",
    purple: "text-purple-400 bg-purple-500/10 border-purple-500/30",
  };
  return (
    <div data-testid={testid} className="fade-up p-5 bg-[#121215] border border-slate-800/80 rounded-xl hover:border-slate-700 transition-colors">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg border flex items-center justify-center ${accents[accent]}`}>
          <Icon className="w-5 h-5" strokeWidth={2} />
        </div>
      </div>
      <div className="mt-4 font-heading text-3xl font-bold text-slate-100">{value}</div>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mt-1">{label}</div>
      {sub && <div className="text-xs text-slate-400 mt-2">{sub}</div>}
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => setError(true));
  }, []);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadCsv("/prospects/export.csv");
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Erreur export CSV");
    } finally {
      setExporting(false);
    }
  };

  if (error) return <div data-testid="dashboard-error" className="p-8 text-rose-400 font-mono text-sm">Impossible de charger les statistiques.</div>;

  if (!stats) return <div className="p-8 text-slate-500 font-mono text-sm">Chargement…</div>;

  const stageData = Object.entries(stats.by_stage || {}).map(([k, v]) => ({
    name: STAGE_LABELS[k]?.label || k,
    count: v,
  }));

  return (
    <div className="p-6 lg:p-8 max-w-[1600px]">
      <header className="mb-8 flex items-end justify-between">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-2">// Dashboard</div>
          <h1 className="font-heading text-4xl font-extrabold text-slate-100 tracking-tight">Vue d'ensemble</h1>
          <p className="text-slate-400 mt-1.5 text-sm">Suivi de votre prospection en Île-de-France en temps réel.</p>
        </div>
        <button data-testid="dashboard-export-csv-button" onClick={exportCsv} disabled={exporting} className="px-4 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 text-sm font-medium flex items-center gap-2 transition-colors disabled:opacity-60">
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Exporter CSV
        </button>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat testid="stat-prospects-count" icon={Users} label="Prospects" value={stats.total_prospects} accent="cyan" />
        <Stat testid="stat-contacted" icon={Target} label="Contactés" value={stats.contacted} accent="amber" sub={`${stats.by_stage?.rdv || 0} RDV planifiés`} />
        <Stat testid="stat-conversion" icon={TrendingUp} label="Conversion" value={`${stats.conversion_rate}%`} accent="emerald" sub={`${stats.by_stage?.gagnes || 0} clients gagnés`} />
        <Stat testid="stat-pipeline-value" icon={Euro} label="Pipeline (€)" value={`${(stats.total_pipeline_value || 0).toLocaleString("fr-FR")}`} accent="purple" sub={`${(stats.won_value || 0).toLocaleString("fr-FR")} € gagnés`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 p-6 bg-[#121215] border border-slate-800/80 rounded-xl fade-up stagger-1">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Nouveaux prospects</div>
              <div className="font-heading text-lg font-semibold text-slate-100">7 derniers jours</div>
            </div>
            <Sparkles className="w-4 h-4 text-cyan-400" />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={stats.timeseries || []}>
              <defs>
                <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0d0d12", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#f8fafc" }} />
              <Area type="monotone" dataKey="count" stroke="#06b6d4" strokeWidth={2} fill="url(#grad)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl fade-up stagger-2">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Répartition pipeline</div>
          <div className="font-heading text-lg font-semibold text-slate-100 mb-4">Par étape</div>
          <div className="space-y-3">
            {Object.entries(stats.by_stage || {}).map(([k, v]) => {
              const cfg = STAGE_LABELS[k];
              const pct = stats.total_prospects ? (v / stats.total_prospects) * 100 : 0;
              return (
                <div key={k} data-testid={`stage-count-${k}`}>
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className={cfg?.text}>{cfg?.label}</span>
                    <span className="font-mono text-slate-400">{v}</span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div className={`h-full ${cfg?.color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-6 pt-4 border-t border-slate-800 flex items-center justify-between">
            <span className="text-xs text-slate-500">Relances en attente</span>
            <span data-testid="stat-tasks-pending" className="font-mono text-amber-400 text-lg font-semibold">{stats.tasks_pending}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <div className="lg:col-span-2 p-6 bg-[#121215] border border-amber-500/30 rounded-xl fade-up stagger-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-amber-400/80 flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> Prospects chauds</div>
              <div className="font-heading text-lg font-semibold text-slate-100">Ils ont ouvert vos emails — relancez-les en priorité</div>
            </div>
            <div className="text-right">
              <div data-testid="stat-open-rate" className="font-heading text-2xl font-bold text-amber-400">{stats.open_rate}%</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">taux d'ouverture · {stats.emails_opened}/{stats.emails_sent}</div>
            </div>
          </div>
          {(stats.hot_prospects || []).length === 0 ? (
            <div data-testid="hot-prospects-empty" className="text-center py-8 text-xs font-mono text-slate-600">
              <MailOpen className="w-6 h-6 mx-auto mb-2 text-slate-700" />
              Aucune ouverture détectée pour l'instant. Les ouvertures apparaîtront ici dès qu'un prospect lira un de vos emails.
            </div>
          ) : (
            <div className="space-y-2">
              {stats.hot_prospects.map((h) => (
                <button
                  key={h.prospect_id}
                  data-testid={`hot-prospect-${h.prospect_id}`}
                  onClick={() => nav(`/prospects/${h.prospect_id}`)}
                  className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 hover:border-amber-500/40 transition-colors text-left"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-slate-100 font-medium truncate">{h.company}</div>
                    <div className="text-xs text-slate-500 truncate">{h.contact_name || "—"} · {STAGE_LABELS[h.stage]?.label || h.stage}{h.last_opened_at ? ` · ouvert le ${new Date(h.last_opened_at).toLocaleString("fr-FR")}` : ""}</div>
                  </div>
                  <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0">
                    <Flame className="w-3 h-3" /> {h.email_opens}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl fade-up stagger-3">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-4">Prospects par étape</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={stageData}>
              <CartesianGrid stroke="#1e293b" vertical={false} />
              <XAxis dataKey="name" stroke="#64748b" fontSize={10} tickLine={false} axisLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0d0d12", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} />
              <Bar dataKey="count" fill="#06b6d4" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
